import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { schools, positions, notifications as notifApi, toc, auth, messages } from '@/lib/api';
import { getStoredUser, clearStoredUser, setStoredUser } from '@/lib/auth';
import { 
  Clock, Bell, LogOut, MapPin, CalendarDays, 
  CheckCircle, XCircle, Loader2, Building2, Plus,
  BarChart3, Volume2, VolumeX, RefreshCw, MessageSquare, Send, Mail,
  BellRing, Smartphone, Settings, HelpCircle, BookOpen, Info, User, Phone, X, CalendarRange, Trash2
} from 'lucide-react';
import { playNotificationSound, isSoundEnabled, setSoundEnabled, unlockAudio, sendSystemNotification, showConfirmationNotification } from '@/lib/audioSettings';
import { subscribeToPush, enablePushNotifications, promptInstall } from '@/lib/pwa';
import { TOCMessageDialog, NotificationOnboardingDialog, NotificationInstructionsDialog, DisconnectSchoolDialog } from '@/components/toc';
import PlanLimitTooltip from '@/components/PlanLimitTooltip';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import BrandLogo from '@/components/BrandLogo';
import AppFooter from '@/components/AppFooter';

// Detect platform
const getPlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
  if (/android/i.test(ua)) return 'android';
  return 'desktop';
};

export default function TOCDashboard() {
  const navigate = useNavigate();
  const { planHasFeature, subFeatureMessage } = usePlanLimits();
  const [user, setUser] = useState(getStoredUser());
  const [connectedSchools, setConnectedSchools] = useState([]);
  const [pendingSchools, setPendingSchools] = useState([]);
  const [positionsList, setPositionsList] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showSchoolInfoDialog, setShowSchoolInfoDialog] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [schoolCode, setSchoolCode] = useState('');
  const [hideFilled, setHideFilled] = useState(false);
  const [hideDeclined, setHideDeclined] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(() => isSoundEnabled());
  const [isConnecting, setIsConnecting] = useState(false);
  const [respondingTo, setRespondingTo] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Availability status (decline all for today)
  const [declinedAllToday, setDeclinedAllToday] = useState(false);
  const [isTogglingAvailability, setIsTogglingAvailability] = useState(false);
  
  // Notification permission states
  const [showNotifOnboarding, setShowNotifOnboarding] = useState(false);
  const [showNotifInstructions, setShowNotifInstructions] = useState(false);
  const [notifPermissionStatus, setNotifPermissionStatus] = useState('default');
  const platform = getPlatform();
  
  // Date selection for unavailability (multi-select calendar)
  const [showMyCalendar, setShowMyCalendar] = useState(false);
  const [isTogglingDate, setIsTogglingDate] = useState(false);
  
  // Disconnect from school states
  const [schoolToDisconnect, setSchoolToDisconnect] = useState(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  
  const lastPositionCount = useRef(-1);
  const lastNotificationCount = useRef(-1);
  
  // Handle audio toggle with persistence
  const handleAudioToggle = useCallback(() => {
    unlockAudio(); // Unlock Web Audio API on user gesture (required for iOS)
    const newValue = !audioEnabled;
    setAudioEnabled(newValue);
    setSoundEnabled(newValue);
  }, [audioEnabled]);
  
  // Unlock audio context on any user interaction with the page
  useEffect(() => {
    const unlock = () => unlockAudio();
    document.addEventListener('touchstart', unlock, { once: true });
    document.addEventListener('click', unlock, { once: true });
    return () => {
      document.removeEventListener('touchstart', unlock);
      document.removeEventListener('click', unlock);
    };
  }, []);
  
  const fetchData = useCallback(async () => {
    try {
      const [schoolsRes, positionsRes, notifsRes, msgCountRes, pendingSchoolsRes, availabilityRes, userRes] = await Promise.all([
        schools.list(),
        positions.list({ include_filled: !hideFilled }),
        notifApi.list(),
        messages.getUnreadCount(),
        toc.getPendingSchools().catch(() => ({ data: [] })),
        toc.getAvailabilityStatus().catch(() => ({ data: { status: 'available' } })),
        auth.me().catch(() => ({ data: null }))
      ]);
      
      setConnectedSchools(schoolsRes.data);
      setPositionsList(positionsRes.data);
      setNotifications(notifsRes.data);
      setUnreadMessages(msgCountRes.data.unread_count);
      setPendingSchools(pendingSchoolsRes.data);
      setDeclinedAllToday(availabilityRes.data.status === 'declined_all_today');
      
      // Update user state with fresh data (for declined_dates etc.)
      if (userRes.data) {
        setUser(userRes.data);
        setStoredUser(userRes.data);
      }
      
      // Mark positions as viewed
      for (const pos of positionsRes.data) {
        if (pos.status === 'open' && !pos.viewed_by?.includes(user?.user_id)) {
          positions.markViewed(pos.position_id).catch(() => {});
        }
      }
      
      // Play sound if new positions (and not declined all today)
      const openPositions = positionsRes.data.filter(p => p.status === 'open');
      const newPositionCount = openPositions.length;
      
      if (lastPositionCount.current === -1) {
        // First fetch — just set baseline, don't play sound
        lastPositionCount.current = newPositionCount;
      } else if (newPositionCount > lastPositionCount.current && availabilityRes.data.status !== 'declined_all_today') {
        // New position appeared since last poll
        const newestPosition = openPositions.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )[0];
        
        // Use system notification (works when backgrounded/screen off) + in-app sound
        sendSystemNotification(
          'New Position Available!',
          `${newestPosition?.school_name || 'A school'} has posted a new substitute position.`,
          {
            // Same tag the backend derives for this posting, so the in-app
            // notification and the web push notification collapse into one
            // instead of alerting twice.
            tag: `expresssub-new_position-${newestPosition?.position_id}`,
            url: '/toc'
          }
        );
        
        toast.info('New position available!', {
          description: `${newestPosition?.school_name || 'A school'} has posted a new substitute position.`,
          duration: 10000,
          action: {
            label: 'View Position',
            onClick: () => {
              const positionCard = document.querySelector(`[data-testid="position-card-${newestPosition?.position_id}"]`);
              if (positionCard) {
                positionCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
                positionCard.classList.add('ring-2', 'ring-emerald-500');
                setTimeout(() => positionCard.classList.remove('ring-2', 'ring-emerald-500'), 3000);
              }
            }
          }
        });
        lastPositionCount.current = newPositionCount;
      } else {
        lastPositionCount.current = newPositionCount;
      }
      
      // Play sound for new notifications (messages, reminders, etc.)
      const unreadNotifications = notifsRes.data.filter(n => !n.read);
      const newNotificationCount = unreadNotifications.length;
      
      if (lastNotificationCount.current === -1) {
        // First fetch — just set baseline, don't play sound
        lastNotificationCount.current = newNotificationCount;
      } else if (newNotificationCount > lastNotificationCount.current) {
        // Find the newest unread notification
        const newestNotification = unreadNotifications.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        )[0];
        
        // Play sound for message notifications and position reminders
        if (newestNotification && (
          newestNotification.type === 'message' || 
          newestNotification.type === 'position_reminder'
        )) {
          // Use system notification for reminders (works when backgrounded)
          if (newestNotification.type === 'position_reminder') {
            sendSystemNotification(
              'Position Reminder',
              newestNotification.message || 'A position is still waiting for your response!',
              {
                tag: `expresssub-position_reminder-${newestNotification.position_id || newestNotification.notification_id}`,
                url: '/toc'
              }
            );
          } else {
            // For messages, play in-app sound + system notification
            sendSystemNotification(
              'New Message',
              newestNotification.message?.substring(0, 100) || 'You have a new message',
              {
                tag: `expresssub-message-${newestNotification.notification_id}`,
                url: '/toc'
              }
            );
          }
          
          // Show toast for message notifications
          if (newestNotification.type === 'message') {
            toast.info('New message received!', {
              description: newestNotification.message?.substring(0, 100) || 'You have a new message',
              duration: 8000
            });
          }
        }
        lastNotificationCount.current = newNotificationCount;
      } else {
        lastNotificationCount.current = newNotificationCount;
      }
    } catch (error) {
      // Silent fail - data will be retried on next poll
    } finally {
      setIsLoading(false);
    }
  }, [hideFilled, user?.user_id]);
  
  useEffect(() => {
    fetchData();
    // Poll for updates every 15 seconds for TOCs
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);
  
  // Check notification permission on mount and subscribe to push if granted
  useEffect(() => {
    if ('Notification' in window) {
      setNotifPermissionStatus(Notification.permission);
      
      // If already granted, subscribe to push silently
      if (Notification.permission === 'granted') {
        const token = localStorage.getItem('token');
        if (token) subscribeToPush(token);
      }
      
      // Show onboarding if permission not yet granted and not dismissed before.
      // Also for 'denied': a blocked user otherwise has no way to find out how
      // to turn notifications back on (KAN-132).
      const dismissed = localStorage.getItem('notif_onboarding_dismissed');
      if ((Notification.permission === 'default' || Notification.permission === 'denied') && !dismissed) {
        // Show after a short delay so the page loads first
        const timer = setTimeout(() => setShowNotifOnboarding(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, []);
  
  const handleInstallApp = async () => {
    const result = await promptInstall();
    if (result.outcome === 'accepted') {
      // Installed. Now request the permission — the installed web app is what
      // makes the browser deliver the notifications (KAN-132).
      await handleEnableNotifications();
      return;
    }
    // No one-tap prompt available (iOS Safari, or Chrome without
    // beforeinstallprompt): show the manual instructions instead.
    setShowNotifInstructions(true);
  };

  const handleEnableNotifications = async () => {
    // The push subscription is registered inside this call, before anything
    // cosmetic can run. Previously this handler raised a `new Notification()`
    // confirmation first, which throws a TypeError in nearly all mobile
    // browsers — the throw aborted the handler before subscribeToPush() ran,
    // so the permission was granted but no subscription was ever created
    // (KAN-132).
    const { permission, subscription } = await enablePushNotifications(
      localStorage.getItem('token')
    );
    setNotifPermissionStatus(permission === 'error' ? 'default' : permission);

    if (permission === 'granted') {
      toast.success('Notifications enabled!');

      // Show platform-specific instructions
      setShowNotifOnboarding(false);
      setShowNotifInstructions(true);

      // Best-effort confirmation notification. It must never affect the flow
      // above, so it is not awaited and cannot throw.
      showConfirmationNotification(
        'Notifications Enabled!',
        'You will now receive alerts for new positions.'
      );
    } else if (permission === 'denied') {
      toast.error('Notifications blocked', {
        description: 'Please enable notifications in your browser settings.'
      });
      setShowNotifOnboarding(false);
      // Deliberately NOT dismissed: the user was blocked by the browser, they
      // did not opt out. The onboarding reappears with instructions on how to
      // turn notifications back on (KAN-132).
    } else if (permission === 'error') {
      toast.error('Could not enable notifications');
    }

    // Never leave the subscription promise unhandled.
    subscription.catch(() => {});
  };
  
  const handleDismissOnboarding = () => {
    setShowNotifOnboarding(false);
    localStorage.setItem('notif_onboarding_dismissed', 'true');
  };
  
  const handleCloseInstructions = () => {
    setShowNotifInstructions(false);
    localStorage.setItem('notif_onboarding_dismissed', 'true');
  };
  
  const handleDeclineAllToday = async () => {
    setIsTogglingAvailability(true);
    try {
      const action = declinedAllToday ? 'undo' : 'decline';
      const response = await toc.declineAllToday(action);
      
      if (action === 'decline') {
        setDeclinedAllToday(true);
        toast.success('You have declined all positions for today', {
          description: 'Your connected schools have been notified.'
        });
      } else {
        setDeclinedAllToday(false);
        toast.success('You are now available for positions again');
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update availability');
    } finally {
      setIsTogglingAvailability(false);
    }
  };
  
  // Toggle a single date's availability
  const handleToggleDate = async (date) => {
    if (!date) return;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date < today) {
      toast.error('Cannot modify past dates');
      return;
    }
    
    setIsTogglingDate(true);
    try {
      const response = await toc.toggleDate(dateStr);
      const action = response.data.action;
      const formattedDate = format(date, 'EEEE, MMM d');
      
      if (action === 'added') {
        toast.success(`Set unavailable: ${formattedDate}`);
      } else {
        toast.success(`Now available: ${formattedDate}`);
      }
      
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update availability');
    } finally {
      setIsTogglingDate(false);
    }
  };
  
  const handleDisconnectSchool = async () => {
    if (!schoolToDisconnect) return;
    
    setIsDisconnecting(true);
    try {
      await toc.disconnect(schoolToDisconnect.school_id);
      toast.success(`Disconnected from ${schoolToDisconnect.name}`);
      setSchoolToDisconnect(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setIsDisconnecting(false);
    }
  };
  
  const handleConnect = async (e) => {
    e.preventDefault();
    if (!schoolCode.trim()) return;
    
    setIsConnecting(true);
    try {
      const response = await toc.connect(schoolCode.trim());
      const status = response.data.status;
      const schoolName = response.data.school.name;
      const message = response.data.message;
      
      if (status === 'pending') {
        // Check if this is a new request or existing pending
        if (message === 'Request already pending') {
          toast.info(`Request already pending for ${schoolName}`, {
            description: 'Please wait for the administrator to review your request.'
          });
        } else {
          toast.success(`Request sent to ${schoolName}!`, {
            description: 'The school administrator will review your request.'
          });
        }
      } else if (status === 'connected') {
        toast.info(`You are already connected to ${schoolName}`, {
          description: 'This school is already in your list.'
        });
      } else {
        toast.success(`Connected to ${schoolName}!`);
      }
      
      setShowConnectDialog(false);
      setSchoolCode('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid school code');
    } finally {
      setIsConnecting(false);
    }
  };
  
  const handleRespond = async (positionId, response) => {
    setRespondingTo(positionId);
    try {
      await positions.respond(positionId, response);
      toast.success(response === 'accept' ? 'Position accepted!' : 'Position declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    } finally {
      setRespondingTo(null);
    }
  };
  
  const handleDismissPosition = async (positionId) => {
    try {
      await positions.dismiss(positionId);
      toast.success('Position removed from your list');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss position');
    }
  };
  
  const handleOpenMessage = async (school) => {
    setSelectedSchool(school);
    setMessageText('');
    setShowMessageDialog(true);
    
    try {
      const res = await messages.list(school.school_id);
      setConversation(res.data);
    } catch (error) {
      // Silent fail - empty conversation will be shown
    }
  };
  
  // Poll for new messages when message dialog is open
  useEffect(() => {
    if (!showMessageDialog || !selectedSchool) return;
    
    const pollMessages = async () => {
      try {
        const res = await messages.list(selectedSchool.school_id);
        setConversation(res.data);
      } catch (_e) {
        // Polling failure - will retry on next interval
      }
    };
    
    // Poll every 5 seconds
    const interval = setInterval(pollMessages, 5000);
    
    return () => clearInterval(interval);
  }, [showMessageDialog, selectedSchool]);
  
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedSchool) return;
    setIsSending(true);
    
    try {
      const res = await messages.send(selectedSchool.school_id, messageText);
      setConversation(prev => [res.data, ...prev]);
      setMessageText('');
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      await auth.logout();
    } catch (_e) {
      // Logout API may fail if token expired - proceed with local cleanup
    }
    clearStoredUser();
    navigate('/');
  };
  
  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);
  
  // Memoize filtered positions
  const displayPositions = useMemo(() => {
    return positionsList.filter(p => {
      if (hideFilled && p.status === 'filled') return false;
      if (p.dismissed_by?.includes(user?.user_id)) return false;
      if (hideDeclined && p.responses?.[user?.user_id] === 'decline') return false;
      return true;
    });
  }, [positionsList, hideFilled, hideDeclined, user?.user_id]);
  
  const openPositions = useMemo(() => displayPositions.filter(p => p.status === 'open'), [displayPositions]);
  const myAcceptedPositions = useMemo(() => positionsList.filter(p => p.accepted_by === user?.user_id), [positionsList, user?.user_id]);
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-emerald-600 text-white sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BrandLogo variant="on-green" size="md" />
            <Badge variant="secondary" className="ml-1 bg-emerald-700 text-emerald-100 text-xs">Sub</Badge>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Help Menu - Hidden on very small screens, accessible via User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:bg-emerald-700 hidden sm:flex"
                  data-testid="help-menu-btn"
                >
                  <HelpCircle className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/guides/sub" className="flex items-center gap-2 cursor-pointer">
                    <BookOpen className="h-4 w-4" />
                    User Guide
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/onboarding/sub" className="flex items-center gap-2 cursor-pointer">
                    <HelpCircle className="h-4 w-4" />
                    Getting Started
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Audio Toggle - Always visible (important for Subs) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="text-white hover:bg-emerald-700"
                  data-testid="audio-toggle-btn"
                >
                  {audioEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={handleAudioToggle} className="cursor-pointer">
                  {audioEnabled ? (
                    <>
                      <VolumeX className="h-4 w-4 mr-2" />
                      Mute Sounds
                    </>
                  ) : (
                    <>
                      <Volume2 className="h-4 w-4 mr-2" />
                      Enable Sounds
                    </>
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/toc/settings/notifications" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" />
                    Sound Settings
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Popover open={showNotifications} onOpenChange={setShowNotifications}>
              <PopoverTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="relative text-white hover:bg-emerald-700"
                  data-testid="notifications-btn"
                >
                  <Bell className="h-5 w-5" />
                  {unreadCount > 0 && (
                    <span className="notification-badge">{unreadCount}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="end">
                <div className="p-3 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-xs h-7"
                        onClick={async () => {
                          await notifApi.markAllRead();
                          fetchData();
                        }}
                      >
                        Mark all read
                      </Button>
                    )}
                  </div>
                </div>
                <ScrollArea className="h-80">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-slate-500">
                      <Bell className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p className="text-sm">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {notifications.slice(0, 20).map((notif) => (
                        <div 
                          key={notif.notification_id}
                          className={`p-3 hover:bg-slate-50 cursor-pointer ${!notif.read ? 'bg-emerald-50' : ''}`}
                          onClick={async () => {
                            if (!notif.read) {
                              await notifApi.markRead(notif.notification_id);
                              fetchData();
                            }
                            // If it's a message notification, open message dialog directly
                            if (notif.type === 'new_message') {
                              // Use school_id from notification if available, otherwise try to find from message
                              let school = null;
                              if (notif.school_id) {
                                school = connectedSchools.find(s => s.school_id === notif.school_id);
                              }
                              if (!school) {
                                // Fallback: try to find school from notification message text
                                school = connectedSchools.find(s => notif.message?.includes(s.name));
                              }
                              if (school) {
                                // Use handleOpenMessage to properly load conversation
                                await handleOpenMessage(school);
                                setShowNotifications(false);
                              }
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              (() => {
                                if (notif.type === 'new_message') return 'bg-blue-100';
                                if (notif.type === 'new_position') return 'bg-emerald-100';
                                if (notif.type === 'position_filled') return 'bg-amber-100';
                                return 'bg-slate-100';
                              })()
                            }`}>
                              {(() => {
                                if (notif.type === 'new_message') return <Mail className="h-4 w-4 text-blue-600" />;
                                if (notif.type === 'new_position') return <CalendarDays className="h-4 w-4 text-emerald-600" />;
                                return <Bell className="h-4 w-4 text-slate-600" />;
                              })()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${!notif.read ? 'font-medium text-slate-900' : 'text-slate-600'}`}>
                                {notif.message}
                              </p>
                              <p className="text-xs text-slate-400 mt-1">
                                {formatDistanceToNow(new Date(notif.created_at), { addSuffix: true })}
                              </p>
                            </div>
                            {!notif.read && (
                              <div className="h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>
            
            {/* Profile & Logout - Collapsed into User Menu on mobile */}
            <div className="hidden sm:flex items-center gap-1">
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-emerald-700"
                onClick={() => navigate('/profile')}
                data-testid="profile-btn"
              >
                <User className="h-5 w-5" />
              </Button>
              
              <Button 
                variant="ghost" 
                size="icon"
                className="text-white hover:bg-emerald-700"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Mobile User Menu - Contains Profile, Help, Logout */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="text-white hover:bg-emerald-700 sm:hidden"
                  data-testid="mobile-user-menu-btn"
                >
                  <User className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <User className="h-4 w-4 mr-2" />
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/guides/sub" className="flex items-center gap-2 cursor-pointer">
                    <BookOpen className="h-4 w-4" />
                    User Guide
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/onboarding/sub" className="flex items-center gap-2 cursor-pointer">
                    <HelpCircle className="h-4 w-4" />
                    Getting Started
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleLogout} 
                  className="cursor-pointer text-red-600 focus:text-red-600"
                  data-testid="mobile-logout-btn"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      
      {/* Main Content - Mobile-first "Gig Feed" layout */}
      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Welcome & Stats */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
            Hi, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600 mt-1">
            {openPositions.length} open positions available
          </p>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-emerald-600">{openPositions.length}</p>
              <p className="text-xs text-slate-500">Available</p>
            </CardContent>
          </Card>
          <Card 
            className="border-slate-200 cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/50 transition-colors"
            onClick={() => setShowMyCalendar(true)}
            data-testid="my-jobs-card"
          >
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{myAcceptedPositions.length}</p>
              <p className="text-xs text-slate-500">My Jobs</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-900">{connectedSchools.length}</p>
              <p className="text-xs text-slate-500">Schools</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Dialog open={showConnectDialog} onOpenChange={setShowConnectDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="connect-school-btn">
                  <Plus className="h-4 w-4 mr-1" />
                  Add School
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-sm">
                <DialogHeader>
                  <DialogTitle className="font-['Manrope']">Connect to School</DialogTitle>
                  <DialogDescription>
                    Enter the school code provided by your employer
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleConnect} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="school-code">School Code</Label>
                    <Input
                      id="school-code"
                      value={schoolCode}
                      onChange={(e) => setSchoolCode(e.target.value)}
                      placeholder="Enter code (e.g., 12345678)"
                      maxLength={10}
                      required
                      data-testid="school-code-input"
                    />
                    <p className="text-xs text-slate-500">
                      Each school can only be added once to your list
                    </p>
                  </div>
                  <DialogFooter>
                    <Button 
                      type="submit" 
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      disabled={isConnecting}
                      data-testid="submit-school-code-btn"
                    >
                      {isConnecting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        'Request to Join'
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            
            {/* My Calendar Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowMyCalendar(true)}
              data-testid="my-calendar-btn"
            >
              <CalendarRange className="h-4 w-4 mr-1" />
              My Calendar
            </Button>
            
            <Link to="/toc/analytics">
              <Button variant="outline" size="sm" data-testid="toc-analytics-btn">
                <BarChart3 className="h-4 w-4 mr-1" />
                Stats
              </Button>
            </Link>
            
            {/* Help Menu (sidebar style) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="sidebar-help-btn">
                  <BookOpen className="h-4 w-4 mr-1" />
                  Help
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem asChild>
                  <Link to="/guides/sub" className="flex items-center gap-2 cursor-pointer">
                    <BookOpen className="h-4 w-4" />
                    User Guide
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/onboarding/sub" className="flex items-center gap-2 cursor-pointer">
                    <HelpCircle className="h-4 w-4" />
                    Getting Started
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* Unavailable Dates Banner */}
        {(declinedAllToday || (user?.declined_dates && user.declined_dates.length > 0)) && (
          <Card className="border-rose-300 bg-rose-50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-rose-100 rounded-full p-2">
                    <XCircle className="h-5 w-5 text-rose-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-rose-900">
                      {declinedAllToday ? "You're unavailable today" : "You have unavailable dates set"}
                    </p>
                    <p className="text-sm text-rose-700">
                      {declinedAllToday && "Today. "}
                      {user?.declined_dates?.length > 0 && (
                        <>Dates: {user.declined_dates.slice(0, 5).map(d => format(parseISO(d), 'MMM d')).join(', ')}
                        {user.declined_dates.length > 5 && ` +${user.declined_dates.length - 5} more`}</>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-rose-600 hover:bg-rose-100"
                  onClick={() => setShowMyCalendar(true)}
                  data-testid="manage-unavailable-btn"
                >
                  Manage
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Connected Schools with Message Option */}
        {connectedSchools.length > 0 && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-slate-700 mb-2">Your Schools</h2>
            <div className="flex flex-wrap gap-2">
              {connectedSchools.map(school => (
                <div key={school.school_id} className="flex items-center gap-1 group">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedSchool(school);
                      setShowSchoolInfoDialog(true);
                    }}
                    className="flex items-center gap-1"
                    data-testid={`school-info-${school.school_id}`}
                  >
                    <Building2 className="h-3 w-3" />
                    {school.name}
                    <Info className="h-3 w-3 ml-1 text-slate-400" />
                  </Button>
                  <PlanLimitTooltip
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => handleOpenMessage(school)}
                    data-testid={`school-message-${school.school_id}`}
                    disabled={!planHasFeature(school.plan_id, 'in_app_messaging')}
                    audience="sub"
                    message={subFeatureMessage('in_app_messaging')}
                  >
                    <MessageSquare className="h-4 w-4" />
                  </PlanLimitTooltip>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => setSchoolToDisconnect(school)}
                    data-testid={`school-disconnect-${school.school_id}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Pending Schools */}
        {pendingSchools.length > 0 && (
          <Card className="border-amber-300 bg-amber-50 mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-amber-100 rounded-full p-2">
                  <Clock className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">
                    {pendingSchools.length} Pending Request{pendingSchools.length !== 1 ? 's' : ''}
                  </p>
                  <p className="text-sm text-amber-700">
                    Waiting for school administrator approval
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                {pendingSchools.map((school) => (
                  <div key={school.school_id} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-amber-200">
                    <Building2 className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="font-medium text-slate-900">{school.name}</p>
                      <p className="text-xs text-slate-500">{school.address}</p>
                    </div>
                    <Badge variant="outline" className="ml-auto bg-amber-100 text-amber-700 border-amber-300">
                      Pending
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Connected Schools */}
        {connectedSchools.length === 0 && pendingSchools.length === 0 ? (
          <Card className="border-slate-200 mb-6">
            <CardContent className="p-8 text-center">
              <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No schools connected</h3>
              <p className="text-sm text-slate-500 mb-4">
                Enter a school code to start receiving position notifications
              </p>
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowConnectDialog(true)}
                data-testid="connect-first-school-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Connect to School
              </Button>
            </CardContent>
          </Card>
        ) : connectedSchools.length === 0 && pendingSchools.length > 0 ? (
          <Card className="border-slate-200 mb-6">
            <CardContent className="p-8 text-center">
              <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No positions yet</h3>
              <p className="text-sm text-slate-500 mb-4">
                Once your school requests are approved, you will see available positions here
              </p>
              <Button 
                variant="outline"
                onClick={() => setShowConnectDialog(true)}
                data-testid="connect-another-school-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Connect to Another School
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Positions Feed */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900 font-['Manrope']">
                  Available Positions
                </h2>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={fetchData}
                  data-testid="refresh-positions-btn"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Filter Toggles */}
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Switch
                    id="hide-filled"
                    checked={hideFilled}
                    onCheckedChange={setHideFilled}
                    data-testid="hide-filled-switch"
                  />
                  <Label htmlFor="hide-filled" className="text-slate-600 cursor-pointer">
                    Hide Filled
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="hide-declined"
                    checked={hideDeclined}
                    onCheckedChange={setHideDeclined}
                    data-testid="hide-declined-switch"
                  />
                  <Label htmlFor="hide-declined" className="text-slate-600 cursor-pointer">
                    Hide Declined
                  </Label>
                </div>
              </div>
              
              {displayPositions.length === 0 ? (
                <Card className="border-slate-200">
                  <CardContent className="p-8 text-center">
                    <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-700 mb-2">No positions available</h3>
                    <p className="text-sm text-slate-500">
                      {(hideFilled || hideDeclined) ? 'Try adjusting your filters above' : 'Check back later for new opportunities'}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                displayPositions.map((position) => {
                  const myResponse = position.responses?.[user?.user_id];
                  const isAcceptedByMe = position.accepted_by === user?.user_id;
                  
                  // Format date display - handle discontinuous dates
                  const dates = position.dates || [];
                  const isMultiDay = dates.length > 1;
                  let shortDateDisplay, fullDateDisplay;
                  
                  if (dates.length === 0) {
                    // Fallback to legacy format
                    const startDate = position.start_date || position.date;
                    shortDateDisplay = format(parseISO(startDate), 'MMM d');
                    fullDateDisplay = format(parseISO(startDate), 'EEEE, MMMM d');
                  } else if (dates.length === 1) {
                    shortDateDisplay = format(parseISO(dates[0]), 'MMM d');
                    fullDateDisplay = format(parseISO(dates[0]), 'EEEE, MMMM d');
                  } else {
                    // Multiple dates - show all dates more clearly
                    shortDateDisplay = `${format(parseISO(dates[0]), 'MMM d')} + ${dates.length - 1} more`;
                    fullDateDisplay = dates.map(d => format(parseISO(d), 'EEEE, MMM d')).join('\n');
                  }
                  
                  return (
                    <Card 
                      key={position.position_id} 
                      className={`border-slate-200 position-card ${
                        position.status === 'filled' ? 'opacity-75' : ''
                      } ${isAcceptedByMe ? 'border-emerald-300 bg-emerald-50/50' : ''}`}
                      data-testid={`position-card-${position.position_id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge className={`status-${position.status}`}>
                              {(() => {
                                if (position.status === 'filled' && isAcceptedByMe) return 'Accepted';
                                if (position.status === 'filled') return 'Filled';
                                return 'Open';
                              })()}
                              </Badge>
                              {isMultiDay && (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  {dates.length} days
                                </Badge>
                              )}
                              {isAcceptedByMe && (
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Your Job
                                </Badge>
                              )}
                            </div>
                            <h3 className="font-bold text-slate-900 text-lg">
                              {position.school_name}
                            </h3>
                          </div>
                          <span className="text-sm font-medium text-slate-600">
                            {shortDateDisplay}
                          </span>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          <p className="text-sm text-slate-600 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-slate-400" />
                            {position.school_address}
                          </p>
                          <p className="text-sm text-slate-600 flex items-center gap-2">
                            <Clock className="h-4 w-4 text-slate-400" />
                            {position.start_time} - {position.end_time}
                          </p>
                          <p className="text-sm text-slate-600 flex items-center gap-2">
                            <CalendarDays className="h-4 w-4 text-slate-400" />
                            {position.grade_level?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'General'}
                          </p>
                        </div>
                        
                        {position.description && (
                          <p className="text-sm text-slate-500 mb-4 bg-slate-50 rounded p-3">
                            {position.description}
                          </p>
                        )}
                        
                        {/* Action Buttons - Show if open and not yet accepted by anyone */}
                        {position.status === 'open' && myResponse !== 'accept' && (
                          <div className="flex gap-3">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button 
                                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                  disabled={respondingTo === position.position_id}
                                  data-testid={`accept-position-${position.position_id}`}
                                >
                                  {respondingTo === position.position_id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      {isMultiDay ? (
                                        <>
                                          <Info className="h-4 w-4 mr-2" />
                                          {myResponse === 'decline' ? 'View Details' : 'View Details'}
                                        </>
                                      ) : (
                                        <>
                                          <CheckCircle className="h-4 w-4 mr-2" />
                                          {myResponse === 'decline' ? 'View Details' : 'Accept'}
                                        </>
                                      )}
                                    </>
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {isMultiDay ? 'Position Details' : 'Accept this position?'}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription asChild>
                                    <div className="space-y-3">
                                      <p className="font-medium text-slate-900">
                                        {position.school_name}
                                      </p>
                                      {isMultiDay ? (
                                        <div className="bg-slate-50 rounded-lg p-3">
                                          <p className="text-sm font-medium text-slate-700 mb-2">
                                            This is a {dates.length}-day position:
                                          </p>
                                          <ul className="space-y-1">
                                            {dates.map((d) => (
                                              <li key={d} className="text-sm text-slate-600 flex items-center gap-2">
                                                <CalendarDays className="h-3 w-3 text-slate-400" />
                                                {format(parseISO(d), 'EEEE, MMMM d, yyyy')}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      ) : (
                                        <p>
                                          Date: {fullDateDisplay}
                                        </p>
                                      )}
                                      <p className="text-sm text-slate-600">
                                        Time: {position.start_time} - {position.end_time}
                                      </p>
                                      <p className="text-sm text-slate-600">
                                        Category: {position.grade_level?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'General'}
                                      </p>
                                      {position.description && (
                                        <p className="text-sm text-slate-500 bg-slate-50 rounded p-2">
                                          {position.description}
                                        </p>
                                      )}
                                    </div>
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-emerald-600 hover:bg-emerald-700"
                                    onClick={() => handleRespond(position.position_id, 'accept')}
                                  >
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Accept Position
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                            
                            <Button 
                              variant="outline"
                              className={`flex-1 ${myResponse === 'decline' ? 'border-slate-300 text-slate-500' : 'border-rose-200 text-rose-600 hover:bg-rose-50'}`}
                              onClick={() => handleRespond(position.position_id, 'decline')}
                              disabled={respondingTo === position.position_id || myResponse === 'decline'}
                              data-testid={`decline-position-${position.position_id}`}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              {myResponse === 'decline' ? 'Declined' : 'Decline'}
                            </Button>
                          </div>
                        )}
                        
                        {/* Response Status - only show for declined (accept shows buttons to change) */}
                        {myResponse === 'decline' && position.status === 'open' && (
                          <p className="text-xs text-slate-500 mt-2 text-center">
                            Changed your mind? Click "View Details" above.
                          </p>
                        )}
                        
                        {/* Filled by someone else */}
                        {position.status === 'filled' && !isAcceptedByMe && (
                          <div className="flex items-center justify-between gap-2 p-3 rounded-lg bg-amber-50 text-amber-700">
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-sm font-medium">
                                This position has been filled
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                              onClick={() => handleDismissPosition(position.position_id)}
                              data-testid={`dismiss-position-${position.position_id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </>
        )}
      </main>
      <AppFooter role="toc" />
      
      {/* Message Dialog */}
      {/* Message Dialog */}
      <TOCMessageDialog
        open={showMessageDialog}
        onOpenChange={setShowMessageDialog}
        schoolName={selectedSchool?.name}
        conversation={conversation}
        messageText={messageText}
        setMessageText={setMessageText}
        onSendMessage={handleSendMessage}
        onDeleteMessage={async (msgId) => {
          try {
            await messages.deleteMessage(msgId);
            setConversation(prev => prev.filter(m => m.message_id !== msgId));
          } catch (_e) { /* Message may already be deleted */ }
        }}
        isSending={isSending}
        userId={user?.user_id}
        sendDisabled={!planHasFeature(selectedSchool?.plan_id, 'in_app_messaging')}
        disabledMessage={subFeatureMessage('in_app_messaging')}
      />
      
      {/* Notification Onboarding Modal */}
      <NotificationOnboardingDialog
        open={showNotifOnboarding}
        onOpenChange={setShowNotifOnboarding}
        onEnable={handleEnableNotifications}
        onDismiss={handleDismissOnboarding}
        onInstall={handleInstallApp}
        onShowInstructions={() => setShowNotifInstructions(true)}
        permission={notifPermissionStatus}
      />
      
      {/* Platform-Specific Instructions Modal */}
      <NotificationInstructionsDialog
        open={showNotifInstructions}
        onOpenChange={setShowNotifInstructions}
        platform={platform}
        onClose={handleCloseInstructions}
      />

      {/* School Info Dialog */}
      <Dialog open={showSchoolInfoDialog} onOpenChange={setShowSchoolInfoDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Building2 className="h-5 w-5 text-emerald-600" />
              {selectedSchool?.name}
            </DialogTitle>
            <DialogDescription>
              School contact information
            </DialogDescription>
          </DialogHeader>
          
          {selectedSchool && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500">Address</p>
                    <p className="text-sm text-slate-900">{selectedSchool.address}</p>
                  </div>
                </div>
                
                <div className="border-t border-slate-200 pt-3">
                  <p className="text-xs text-slate-500 mb-2 font-medium">Contact Information</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <User className="h-4 w-4 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-900">{selectedSchool.contact_person}</p>
                        {selectedSchool.contact_position && (
                          <p className="text-xs text-slate-500">{selectedSchool.contact_position}</p>
                        )}
                      </div>
                    </div>
                    
                    {selectedSchool.contact_email && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <a 
                          href={`mailto:${selectedSchool.contact_email}`}
                          className="text-sm text-emerald-600 hover:underline"
                        >
                          {selectedSchool.contact_email}
                        </a>
                      </div>
                    )}
                    
                    {selectedSchool.contact_phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-4 w-4 text-slate-400" />
                        <a 
                          href={`tel:${selectedSchool.contact_phone}`}
                          className="text-sm text-emerald-600 hover:underline"
                        >
                          {selectedSchool.contact_phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <PlanLimitTooltip
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowSchoolInfoDialog(false);
                    handleOpenMessage(selectedSchool);
                  }}
                  data-testid="school-info-message-btn"
                  disabled={!planHasFeature(selectedSchool?.plan_id, 'in_app_messaging')}
                  audience="sub"
                  message={subFeatureMessage('in_app_messaging')}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Message
                </PlanLimitTooltip>
                <Button 
                  onClick={() => setShowSchoolInfoDialog(false)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* My Calendar Dialog */}
      <Dialog open={showMyCalendar} onOpenChange={setShowMyCalendar}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <CalendarRange className="h-5 w-5 text-emerald-600" />
              My Calendar
            </DialogTitle>
            <DialogDescription>
              View your accepted jobs and manage your availability
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Calendar with accepted positions and unavailability */}
            <style>{`
              .my-calendar .rdp-day_unavailable {
                background-color: rgb(225, 29, 72) !important;
                color: white !important;
                font-weight: 600;
              }
              .my-calendar .rdp-day_unavailable:hover {
                background-color: rgb(190, 18, 60) !important;
              }
              .my-calendar .rdp-day_accepted {
                background-color: rgb(16, 185, 129) !important;
                color: white !important;
                font-weight: 600;
              }
              .my-calendar .rdp-day_accepted:hover {
                background-color: rgb(5, 150, 105) !important;
              }
              .my-calendar .rdp-day:not(.rdp-day_unavailable):not(.rdp-day_accepted):hover {
                background-color: rgb(241, 245, 249) !important;
              }
              .my-calendar .rdp-day_disabled {
                opacity: 0.3 !important;
              }
            `}</style>
            
            <Calendar
              mode="single"
              selected={undefined}
              onSelect={(date) => date && handleToggleDate(date)}
              numberOfMonths={1}
              disabled={(date) => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                // Disable past dates and dates with accepted positions (can't toggle those)
                const dateStr = format(date, 'yyyy-MM-dd');
                const hasAcceptedPosition = myAcceptedPositions.some(p => 
                  (p.dates || []).includes(dateStr)
                );
                return date < today || isTogglingDate || hasAcceptedPosition;
              }}
              modifiers={{
                unavailable: (user?.declined_dates || []).map(d => parseISO(d)),
                accepted: myAcceptedPositions.flatMap(p => 
                  (p.dates || []).map(d => parseISO(d))
                )
              }}
              modifiersClassNames={{
                unavailable: 'rdp-day_unavailable',
                accepted: 'rdp-day_accepted'
              }}
              className="rounded-md border my-calendar mx-auto"
              data-testid="my-calendar"
            />
            
            {isTogglingDate && (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating...
              </div>
            )}
            
            {/* Legend */}
            <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-emerald-500"></div>
                <span>Accepted Job</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-rose-600"></div>
                <span>Unavailable</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-4 h-4 rounded bg-white border border-slate-300"></div>
                <span>Available</span>
              </div>
            </div>
            
            {/* Accepted Positions Summary */}
            {myAcceptedPositions.length > 0 && (
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-xs text-emerald-700 mb-2 font-medium flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  {myAcceptedPositions.length} accepted job{myAcceptedPositions.length !== 1 ? 's' : ''}
                </p>
                <div className="space-y-1 max-h-24 overflow-y-auto">
                  {myAcceptedPositions.map((position) => (
                    <div key={position.position_id} className="text-xs text-emerald-600 flex items-center gap-2">
                      <Building2 className="h-3 w-3 flex-shrink-0" />
                      <span className="font-medium">{position.school_name}</span>
                      <span className="text-emerald-500">
                        {(position.dates || []).map(d => format(parseISO(d), 'MMM d')).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Unavailability Section */}
            <div className="border-t pt-4">
              <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-rose-500" />
                Set Unavailability
              </p>
              <p className="text-xs text-slate-500 mb-3">
                Click on calendar dates to mark yourself unavailable
              </p>
              
              {/* Summary of unavailable dates */}
              {user?.declined_dates?.length > 0 && (
                <div className="bg-rose-50 rounded-lg p-3">
                  <p className="text-xs text-rose-700 mb-2 font-medium">
                    {user.declined_dates.filter(d => parseISO(d) >= new Date(new Date().setHours(0,0,0,0))).length} date{user.declined_dates.filter(d => parseISO(d) >= new Date(new Date().setHours(0,0,0,0))).length !== 1 ? 's' : ''} marked unavailable:
                  </p>
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {user.declined_dates
                      .sort()
                      .filter(d => parseISO(d) >= new Date(new Date().setHours(0,0,0,0)))
                      .map((d) => (
                        <Badge 
                          key={d} 
                          variant="secondary" 
                          className="text-xs cursor-pointer bg-rose-100 text-rose-700 hover:bg-rose-200 transition-colors"
                          onClick={() => handleToggleDate(parseISO(d))}
                          data-testid={`unavailable-badge-${d}`}
                        >
                          {format(parseISO(d), 'MMM d')}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowMyCalendar(false)}
              className="w-full sm:w-auto"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect School Confirmation Dialog */}
      {/* Disconnect School Confirmation Dialog */}
      <DisconnectSchoolDialog
        school={schoolToDisconnect}
        onOpenChange={setSchoolToDisconnect}
        onConfirm={handleDisconnectSchool}
        isDisconnecting={isDisconnecting}
      />
    </div>
  );
}
